/**
 * Participant layout calculator.
 *
 * Calculates the position and dimensions of participants in the SVG canvas,
 * ensuring even spacing and proper centering.
 */

import type { Participant } from '../../../parser/types';
import type { LayoutConfig, ParticipantLayout } from './types';

/**
 * Calculates layout positions for participants.
 */
export class ParticipantLayoutCalculator {
  private participants: Participant[];
  private config: LayoutConfig;

  constructor(participants: Participant[], config: LayoutConfig) {
    this.participants = participants;
    this.config = config;
  }

  /**
   * Calculates layouts for all participants.
   *
   * Participants are evenly spaced horizontally and centered as a group
   * within the available width.
   *
   * @returns Map of participant name to layout information
   */
  public calculateLayouts(): Map<string, ParticipantLayout> {
    const layouts = new Map<string, ParticipantLayout>();
    const participantCount = this.participants.length;

    if (participantCount === 0) {
      return layouts;
    }

    // Calculate total width needed for all participants
    // Group width = (count * participantWidth) + ((count - 1) * spacing)
    const groupWidth =
      participantCount * this.config.participantWidth +
      (participantCount - 1) * this.config.participantSpacing;

    // Calculate available width
    const availableWidth =
      this.config.width - this.config.marginLeft - this.config.marginRight;

    // Calculate starting X to center the group
    const startX = this.config.marginLeft + (availableWidth - groupWidth) / 2;

    // Calculate spacing between participant centers
    const centerSpacing =
      this.config.participantSpacing + this.config.participantWidth;

    // Position each participant
    this.participants.forEach((participant, index) => {
      const x = startX + index * centerSpacing;
      const y = this.config.marginTop;
      const centerX = x + this.config.participantWidth / 2;

      layouts.set(participant.name, {
        name: participant.name,
        x,
        y,
        width: this.config.participantWidth,
        height: this.config.participantHeight,
        centerX,
        lifelineStart: y + this.config.participantHeight,
        lifelineEnd: this.config.height - this.config.marginBottom,
        ...(participant.color && { color: participant.color }),
      });
    });

    return layouts;
  }
}
